/* eslint-disable react/no-unused-prop-types */
import { Avatar } from '@mui/material';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { m } from 'framer-motion';
import { capitalize, isEmpty, startCase } from 'lodash';
import posthog from 'posthog-js';
import React, { useMemo } from 'react';
import { varTranHover } from 'src/components/animate';
import ExpandableContent from 'src/components/editor/expandable-editor';
import EventDate from 'src/components/event-date';
import Iconify from 'src/components/iconify';
import Image from 'src/components/image';
import { Lightbox, Slide, useLightBox } from 'src/components/lightbox';
import { useResponsive } from 'src/hooks/use-responsive';
import { useTranslate } from 'src/locales';
import { EventConnectMatch, User } from 'src/types';
import { IEvent, IEventParticipant } from 'src/types/events';
import { Timezone } from 'src/types/timezone';
import { getPictureToDisplay } from 'src/utils/picture';

import { extractProviderName } from '../../utils/url';
import EventTicketList from './event-details-tickets';
import EventMapMarkerPreview from './event-map-marker-preview';
import MessageDisplay from './event-order-status';

export default function EventDetailsContent({ event }: EventDetailsContentProps) {
  const { t: tEvent, i18n } = useTranslate('event');

  const mdDown = useResponsive('down', 'md');

  const provider = useMemo(
    () => (event.url ? extractProviderName(event.url) : 'default'),
    [event.url]
  );

  const slides = useMemo(
    () =>
      [event.cover, ...event.pictures]
        .filter((image): image is typeof image & { url: string } => !!image?.url)
        .map((image) => ({
          src: image.url,
        })),
    [event.cover, event.pictures]
  );
  const hasAnOrder = useMemo(
    () => event.userOrders?.some((order) => order.id > 0),
    [event.userOrders]
  );
  const startDate = useMemo(() => event?.startDate ?? event?.date, [event.startDate, event.date]);

  const isEventActive = useMemo(
    () => new Date(event?.endDate ?? event.date) > new Date(),
    [event.endDate, event.date]
  );
  const parsedDescription = useMemo(
    () => parseDescription(event?.description ?? ''),
    [event?.description]
  );

  const { selected: selectedImage, open: openLightBox, onOpen, onClose } = useLightBox(slides);
  console.log(
    '!event?.ticketSettings?.length && event?.url',
    !event?.ticketSettings?.length,
    !event?.ticketSettings?.length && !!event?.url
  );
  const isDark = !event?.configuration?.displayMode
    ? false
    : event?.configuration?.displayMode !== 'DARK';
  return (
    <Stack
      sx={{ ...(!mdDown && { maxWidth: '60%', minWidth: '60%' }), px: 2.5, pb: 10, pt: 5 }}
      gap={2}
    >
      <RenderHeader title={event.title} isDark={isDark} />
      <RenderDateTime
        startDate={startDate}
        endDate={event.endDate}
        timezone={event.timezone}
        language={i18n.language}
        isDark={isDark}
      />
      {mdDown && (
        <MessageDisplay
          hasPendingRequest={event.hasPendingRequest}
          isDirty={false}
          hasAnOrder={hasAnOrder}
          userOrders={event.userOrders}
          isDark={isDark}
        />
      )}
      {isEventActive && event?.ticketSettings?.length > 0 && (
        <EventTicketList eventId={event.id} isDark={isDark} />
      )}
      {event.description && (
        <ExpandableContent
          title={tEvent('description')}
          content={
            parsedDescription.type === 'object' ? parsedDescription.content : event.description
          }
          isEditorContent={parsedDescription.type === 'object'}
          isDark={isDark}
        />
      )}
      {mdDown && (event.organizer || event?.organizationId) && (
        <RenderSubmitter
          title={event?.organizationId ? tEvent('organized-by') : tEvent('submitted-by')}
          organizer={event.organizer}
          organization={event.organization}
          user={event.user}
          eventId={Number(event.id)}
        />
      )}
      {!event?.ticketSettings?.length && event?.url && (
        <RenderProviders
          title={tEvent('tickets-available-on')}
          url={event.url}
          provider={provider}
          isDark={isDark}
        />
      )}
      <RenderVenue title={tEvent('venue')} venue={event.venue} tEvent={tEvent} isDark={isDark} />
      <EventMapMarkerPreview event={event} onMapClick={() => redirectToMaps(event.venue)} />
      <RenderGallery
        title={tEvent('gallery')}
        slides={slides}
        isDark={isDark}
        selectedImage={selectedImage}
        openLightBox={openLightBox}
        onOpen={onOpen}
        onClose={onClose}
      />
    </Stack>
  );
}

interface DarkModeEnabled {
  isDark?: boolean;
  title?: string;
}

interface DescriptionResult extends DarkModeEnabled {
  type: 'object' | 'json' | 'text';
  content: object | string | any;
}

interface EventDetailsContentProps extends DarkModeEnabled {
  event: IEvent;
}

interface HeaderProps extends DarkModeEnabled {
  title: string;
}

interface DateTimeProps extends DarkModeEnabled {
  startDate: Date;
  endDate?: Date;
  timezone?: Timezone;
  language: string;
}

interface SubmitterProps extends DarkModeEnabled {
  organizer?: IEvent['organizer'];
  organization?: IEvent['organization'];
  user: User | EventConnectMatch['user'] | IEventParticipant;
  eventId?: number;
}

interface ProvidersProps extends DarkModeEnabled {
  title: string;
  url?: string;
  provider: string;
}

interface VenueProps extends DarkModeEnabled {
  venue: IEvent['venue'];
  tEvent: (key: string) => string;
}

interface GalleryProps extends DarkModeEnabled {
  slides: { src: string | undefined }[];
  selectedImage: number;
  openLightBox: boolean;
  onOpen: (src: string) => void;
  onClose: () => void;
}

// Constants
const ICON_SIZE = { width: 30, height: 30 };
const CONTAINER_STYLE = {
  borderRadius: '12px',
  border: '1px solid rgba(249, 194, 75, 0.10)',
  width: '3rem',
  height: '3rem',
};

const CONTAINER_STYLE_DARK = {
  borderRadius: '12px',
  border: '1px solid rgba(249, 194, 75, 0.10)',
  backgroundColor: 'rgba(34, 34, 34, 0.2)', // Transparent white background
  backdropFilter: 'blur(1px)', // Apply blur here
  WebkitBackdropFilter: 'blur(1px)', // Safari support
  width: '3rem',
  height: '3rem',
};

const redirectToMaps = (venue: IEvent['venue']) => {
  if (!isEmpty(venue.place)) {
    window.open(venue.place.url)?.focus();
    return;
  }
  window.open(`https://maps.google.com/?q=${venue.latitude},${venue.longitude}`)?.focus();
};

const parseDescription = (text?: string): DescriptionResult => {
  try {
    if (!text) return { type: 'text', content: text };
    const parsed = JSON.parse(text);
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? { type: 'object', content: parsed }
      : { type: 'json', content: parsed };
  } catch {
    return { type: 'text', content: text };
  }
};

// Components
function RenderHeader({ title, isDark }: HeaderProps) {
  return (
    <Stack direction="row">
      <Typography
        sx={{
          flexGrow: 1,
          ...(isDark && { color: 'black' }),
          fontSize: 35,
          fontFamily: "'Abel', sans-serif",
        }}
      >
        {title}
      </Typography>
    </Stack>
  );
}

function RenderDateTime({ startDate, endDate, timezone, language, isDark }: DateTimeProps) {
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <EventDate startDate={startDate} endDate={endDate} timezone={timezone} />
    </Stack>
  );
}

function RenderSubmitter({
  title,
  organizer,
  organization,
  user,
  isDark,
  eventId,
}: SubmitterProps) {
  return (
    <Stack gap={1}>
      <Typography variant="h5" color={isDark ? '#000' : '#fff'}>
        {title}
      </Typography>
      <Stack spacing={2} direction="row" alignItems="center">
        <Avatar
          src={organizer?.pictureURL ?? organization?.logo?.url ?? getPictureToDisplay(user)}
          sx={{ width: 48, height: 48 }}
        />
        {organizer?.name ? (
          <Typography>{startCase(organizer.name)}</Typography>
        ) : (
          <Stack direction="column" alignItems="flex-start">
            <Link href={organization?.normalizedName ? `/${organization?.normalizedName}` : ''}>
              <Typography>{startCase(organization?.name ?? user?.firstname)}</Typography>
            </Link>
            {eventId === 11707 && <Typography>By The Neighbors & Jamdepaname</Typography>}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

function RenderProviders({ title, url, provider, isDark }: ProvidersProps) {
  return (
    <Stack gap={1}>
      <Typography variant="h5" color={isDark ? '#000' : '#fff'}>
        {title}
      </Typography>
      <Stack spacing={1} direction="row" alignItems="center">
        <Box
          component="img"
          src={`/assets/icons/providers/${provider}.png`}
          sx={{ width: provider.includes('ra.co') ? 34 : 24, height: 24 }}
        />
        <Link
          href={url}
          variant="body2"
          target="_blank"
          rel="noopener"
          onClick={() => url && posthog.capture('go_to_affiliate', { provider, eventURL: url })}
        >
          {capitalize(provider)}
        </Link>
      </Stack>
    </Stack>
  );
}

function RenderVenue({ title, venue, tEvent, isDark }: VenueProps) {
  return (
    <Stack gap={1}>
      <Typography variant="h5" color={isDark ? '#000' : '#fff'}>
        {title}
      </Typography>
      <Stack spacing={2} direction="row" alignItems="center">
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={isDark ? CONTAINER_STYLE_DARK : CONTAINER_STYLE}
        >
          <Iconify icon="basil:location-solid" color="#FFD369" {...ICON_SIZE} />
        </Stack>
        <Stack sx={{ ...(isDark && { color: 'black' }) }}>
          <Typography>{venue.name}</Typography>
          {venue?.place?.rating && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography sx={{ typography: 'body2' }}>{venue.place.rating}</Typography>
              {Array.from({ length: 5 }).map((_, i) => (
                <Iconify
                  key={i}
                  icon={i < Math.floor(venue.place.rating) ? 'eva:star-fill' : 'eva:star-outline'}
                  sx={{ color: 'warning.main' }}
                />
              ))}
              {venue.place.user_ratings_total && (
                <Link sx={{ color: 'text.secondary' }}>
                  ({venue.place.user_ratings_total} {tEvent('reviews')})
                </Link>
              )}
            </Stack>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}

function RenderGallery({
  title,
  slides,
  selectedImage,
  openLightBox,
  isDark,
  onOpen,
  onClose,
}: GalleryProps) {
  return (
    <Stack gap={1}>
      <Typography variant="h5" color={isDark ? '#000' : '#fff'}>
        {title}
      </Typography>
      <Box
        gap={1}
        display="grid"
        gridTemplateColumns={{ xs: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)' }}
        sx={{ mb: { xs: 3, md: 5 } }}
      >
        <Box gap={1} display="grid" gridTemplateColumns="repeat(2, 1fr)">
          {slides.slice(0, 5).map((slide) => (
            <m.div
              key={slide.src}
              whileHover="hover"
              variants={{ hover: { opacity: 0.8 } }}
              transition={varTranHover()}
            >
              <Image
                alt={slide.src}
                src={slide.src}
                ratio="1/1"
                onClick={() => slide.src && onOpen(slide.src)}
                sx={{ borderRadius: 2, cursor: 'pointer' }}
              />
            </m.div>
          ))}
        </Box>
      </Box>
      <Lightbox
        index={selectedImage}
        slides={slides as Slide[]}
        open={openLightBox}
        close={onClose}
      />
    </Stack>
  );
}
